/**
 * TransactionGrid (BUILD_SPEC.md §10.2).
 *
 * Responsive `auto-fill, minmax(420px, 1fr)` grid that targets four
 * columns times three rows = twelve cards on a 65" 1080p TV at typical
 * viewing distance.
 *
 * Scroll behavior: no timed rotation. When a new or updated transaction
 * arrives (new id, or a strictly newer lastChangedAt), the grid scrolls
 * smoothly to the top so staff see the freshest card without hunting.
 * If the user has manually scrolled more than 100 px down, the scroll is
 * suppressed so their reading position is not interrupted.
 *
 * Sort order: cards changed in the last 5 minutes float to the top,
 * then by lastChangedAt desc. The recent-bucket lift keeps newly active
 * cards visible without requiring a scroll.
 */

import { useEffect, useMemo, useRef } from "react";
import type { Action, Transaction } from "@/api/types";
import { TransactionCard } from "./TransactionCard";
import { minutesSince } from "@/lib/dates";

interface GridItem {
  transaction: Transaction;
  actions: Action[];
}

interface Props {
  items: GridItem[];
}

/** Sort comparator -- recent bucket first, then most recently changed. */
function compareForGrid(a: GridItem, b: GridItem): number {
  const aRecent = minutesSince(a.transaction.lastChangedAt) < 5 ? 1 : 0;
  const bRecent = minutesSince(b.transaction.lastChangedAt) < 5 ? 1 : 0;
  if (aRecent !== bRecent) return bRecent - aRecent;
  const at = a.transaction.lastChangedAt ? new Date(a.transaction.lastChangedAt).getTime() : 0;
  const bt = b.transaction.lastChangedAt ? new Date(b.transaction.lastChangedAt).getTime() : 0;
  return bt - at;
}

/** Parse a nullable ISO timestamp to epoch ms; null becomes 0 (oldest). */
function toMs(ts: string | null | undefined): number {
  return ts ? new Date(ts).getTime() : 0;
}

export function TransactionGrid({ items }: Props) {
  const sorted = useMemo(() => [...items].sort(compareForGrid), [items]);

  // The scrolling container is the grid's height-bound viewport.
  const scrollerRef = useRef<HTMLDivElement>(null);

  // Snapshot of the previous items render, keyed by transaction id.
  // Initialized to null so the very first render is treated as no-activity.
  const prevRef = useRef<Map<string, string | null> | null>(null);

  useEffect(() => {
    const prev = prevRef.current;

    // Build the current snapshot before deciding whether to scroll,
    // so the ref is always up to date regardless of the scroll decision.
    const next = new Map<string, string | null>(
      items.map(({ transaction: tx }) => [tx.id, tx.lastChangedAt ?? null]),
    );
    prevRef.current = next;

    // First render: no previous snapshot, so no scroll.
    if (prev === null) return;

    // Detect new activity: a new id, or a strictly newer lastChangedAt.
    let hasActivity = false;
    for (const { transaction: tx } of items) {
      if (!prev.has(tx.id)) {
        hasActivity = true;
        break;
      }
      const prevMs = toMs(prev.get(tx.id));
      const currMs = toMs(tx.lastChangedAt);
      if (currMs > prevMs) {
        hasActivity = true;
        break;
      }
    }

    if (!hasActivity) return;

    // Only scroll to the top when the user is already near the top so
    // we do not yank them away from a card they are reading.
    const el = scrollerRef.current;
    if (!el || el.scrollTop > 100) return;
    el.scrollTo({ top: 0, behavior: "smooth" });
  }, [items]);

  return (
    <div
      ref={scrollerRef}
      className="max-h-[calc(100vh-3.5rem)] overflow-y-auto px-4 py-4"
    >
      <div
        className="grid gap-4"
        style={{
          gridTemplateColumns: "repeat(auto-fill, minmax(min(420px, 100%), 1fr))",
        }}
      >
        {sorted.map(({ transaction, actions }) => (
          <TransactionCard key={transaction.id} transaction={transaction} actions={actions} />
        ))}
      </div>
    </div>
  );
}
